import React, { useState, useEffect } from 'react'
import { Select } from 'antd'
import {
    EditOutlined,
    DeleteOutlined,
    CaretDownOutlined,
    CloseOutlined
} from '@ant-design/icons'

import { StyledButton } from '../../Button'
import { useCaseViewContext } from '../../../context'
import { useClient, usePharmacyNoteConfig } from '../../../hooks'
import { previewComposedNote, templateHasSuffix } from '../../../utils/pharmacyNoteUtils'
import {
    ComposedNotePreview,
    ComposedNoteLabel,
    ComposedNoteBody,
    ComposedNoteCharCount,
} from './Treatments.styles'

export const QueuedPrescription = ({
    background,
    prescription,
    handleSelectPrescription,
    handleRemovePrescription,
    isAssignedProvider,
    onTemplateChange
}) => {
    const { caseDetails, apiClasses } = useCaseViewContext()
    const { clientName } = useClient(caseDetails?.clientId, apiClasses)
    const { pharmacyNoteConfig } = usePharmacyNoteConfig(prescription.pharmacyId, apiClasses)

    const [templatesState, setTemplatesState] = useState({
        options: prescription.templateAlternatives || [],
        mode: 'read',
        searchString: ''
    })

    useEffect(() => {
        setTemplatesState(prev => ({
            ...prev,
            options: prescription.templateAlternatives || [],
            searchString: prescription.templateTitle || prescription.templateName || ''
        }))
    }, [prescription.templateAlternatives, prescription.templateTitle, prescription.templateName])

    const mappedTemplateOptions = (templateAlternatives) => {
        return templateAlternatives.map(template => ({
            label: template.DAW
                ? <span><strong style={{ color: 'red' }}>DAW</strong> {template.title}</span>
                : template.title,
            value: template.title,
            key: template._id
        }))
    }

    const handleSelectTemplate = async (templateTitle) => {
        const selectedTemplate = templatesState.options.find(template => template.title === templateTitle)
        
        if (onTemplateChange) {
            await onTemplateChange({
                template: selectedTemplate,
                templateAlternatives: prescription.templateAlternatives,
                requestedMedication: prescription.requestedMedication,
                prescriptionId: prescription._id
            })
        }

        setTemplatesState(prev => ({ ...prev, mode: 'read' }))
    }

    const ReadMode = () => (
        <>
            <strong
                style={{
                    color: '#7BAD7E',
                    fontSize: '1rem'
                }}
            >
                {prescription.drugName || prescription.name} {prescription.strength}
            </strong>
            <div style={{ display: 'inline-block', marginLeft: '3px'}}>
                <StyledButton
                    size='small'
                    tooltip='Change Template Medication'
                    Icon={CaretDownOutlined}
                    onClick={() => setTemplatesState(prev => ({ ...prev, mode: 'write' }))}
                    style={{ 
                        height: '20px', 
                        width: '20px', 
                        padding: '2px',
                        fontSize: '12px'
                    }}
                />
            </div>
        </>
    )

    const WriteMode = () => (
        <div style={{ display: 'flex', flexDirection: 'row', gap: '5px', alignItems: 'center' }}>
            <Select
                style={{ width: '100%' }}
                options={mappedTemplateOptions(templatesState.options)}
                value={templatesState.searchString}
                onSelect={handleSelectTemplate}
                defaultOpen
                placeholder="Search Templates"
            />
            <div style={{ display: 'inline-block', marginLeft: '3px'}}>
                <StyledButton
                    size='mini'
                    color='red'
                    tooltip='Cancel'
                    Icon={CloseOutlined}
                    onClick={() => setTemplatesState(prev => ({ ...prev, mode: 'read' }))}
                    style={{ borderRadius: '50%' }}
                />
            </div>
        </div>
    )

    return (
        <div style={{
            width: '100%',
            padding: '20px',
            marginBottom: '20px',
            background: background || 'rgba(123, 173, 126, .15)',
            color: '#000',
            borderRadius: '5px',
            display: 'flex',
            flexDirection: 'column',
            gap: '5px'
        }}>
            <div style={{ width: '100%', display: 'flex' }}>
                <div style={{ display: 'flex', width: '100%' }}>
                    <div style={{ width: 'calc(100% - 100px)' }}>
                        { templatesState.mode === 'read' ? <ReadMode /> : <WriteMode /> }
                    </div>
                    {
                        isAssignedProvider && (
                            <div style={{ width: '100px', display: 'flex', justifyContent: 'flex-end', gap: '3px', }}>
                                <StyledButton 
                                    size='small'
                                    tooltip='Edit Prescription'
                                    Icon={EditOutlined}
                                    onClick={handleSelectPrescription}
                                    color='#7BAD7E'
                                />
                                <StyledButton 
                                    size='small' 
                                    tooltip='Delete Prescription'
                                    Icon={DeleteOutlined}
                                    onClick={handleRemovePrescription}
                                    color="red" 
                                />
                            </div>
                        )
                    }
                </div>
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '15px'  }}>
                { prescription.title && <div><strong>Template:</strong> {prescription.title}</div> }
                <div><strong>Pharmacy:</strong> {prescription.pharmacy?.name}</div>
                <div style={{ display: 'flex', gap: '15px', fontWeight: 'bold' }}>
                    <span>Quantity: {prescription.quantity} {prescription.quantityUnits}(s) {prescription.dispense || ''}</span>
                    <span>Refills: {prescription.refills}</span>
                    <span>Supply: {prescription.daysSupply} days</span>
                </div>
                <div><strong>Directions:</strong> {prescription.directions}</div>
                <div><strong>Pharmacy Notes:</strong> {prescription.pharmacyNotes || 'None'}</div>
                {templateHasSuffix(pharmacyNoteConfig?.noteTemplate) && (() => {
                    const composedNote = previewComposedNote(pharmacyNoteConfig.noteTemplate, {
                        pharmacyNotes: prescription.pharmacyNotes || '',
                        daw: prescription.DAW || false,
                        patientId: caseDetails?.patient?._id || '',
                        clientName,
                    })
                    return (
                        <ComposedNotePreview $compact>
                            <ComposedNoteLabel type="secondary">
                                Composed pharmacy note preview
                            </ComposedNoteLabel>
                            <ComposedNoteBody>
                                {composedNote || <ComposedNoteLabel type="secondary">No notes entered yet…</ComposedNoteLabel>}
                            </ComposedNoteBody>
                            <ComposedNoteCharCount>
                                {composedNote.length} / {pharmacyNoteConfig.maxCharacters} chars
                            </ComposedNoteCharCount>
                        </ComposedNotePreview>
                    )
                })()}
                { prescription.DAW && <div style={{ color: 'red'}}><strong>NO SUBSTITUTIONS</strong></div> }
            </div>
        </div>
    )
}
